import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import {
  collection, addDoc, getDocs, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy
} from 'firebase/firestore';

export default function PromptLibrary({ user }) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [msg, setMsg] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({
    title: '',
    useCase: '',
    workflow: '',
    roleTag: '',
    prompt: '',
    outputNote: '',
    chainPrompt: '',
  });

  const collectionRef = collection(db, 'promptLibrary', user.uid, 'prompts');

  useEffect(() => {
    loadPrompts();
  }, [user.uid]);

  const loadPrompts = async () => {
    setLoading(true);
    try {
      const q = query(collectionRef, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setPrompts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading prompts:', err);
    }
    setLoading(false);
  };

  const showMsg_ = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3500);
  };

  const resetForm = () => {
    setForm({ title: '', useCase: '', workflow: '', roleTag: '', prompt: '', outputNote: '', chainPrompt: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.prompt.trim()) {
      showMsg_('error', 'Title and prompt text are required.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(collectionRef, editingId), {
          ...form,
          updatedAt: serverTimestamp(),
        });
        showMsg_('success', 'Prompt updated.');
      } else {
        await addDoc(collectionRef, {
          ...form,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        showMsg_('success', 'Prompt saved to your library.');
      }
      await loadPrompts();
      resetForm();
    } catch (err) {
      showMsg_('error', 'Could not save. Please try again.');
    }
    setSaving(false);
  };

  const handleEdit = (p) => {
    setForm({
      title: p.title || '',
      useCase: p.useCase || '',
      workflow: p.workflow || '',
      roleTag: p.roleTag || '',
      prompt: p.prompt || '',
      outputNote: p.outputNote || '',
      chainPrompt: p.chainPrompt || '',
    });
    setEditingId(p.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await deleteDoc(doc(collectionRef, id));
      setPrompts(prev => prev.filter(p => p.id !== id));
      showMsg_('success', 'Prompt deleted.');
    } catch {
      showMsg_('error', 'Could not delete. Please try again.');
    }
    setDeleting(null);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    showMsg_('success', 'Prompt copied to clipboard.');
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const filtered = prompts.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (p.title || '').toLowerCase().includes(term) ||
      (p.useCase || '').toLowerCase().includes(term) ||
      (p.prompt || '').toLowerCase().includes(term) ||
      (p.workflow || '').toLowerCase().includes(term)
    );
  });

  const formatDate = (ts) => {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div style={s.wrapper}>
      <div style={s.headerRow}>
        <div>
          <div className="page-heading">Prompt Library</div>
          <div className="page-sub">Your personal collection of prompts. Private to you. Build it as you go.</div>
        </div>
        {!showForm && (
          <button style={s.addBtn} onClick={() => setShowForm(true)}>+ Add Prompt</button>
        )}
      </div>

      {msg && (
        <div style={{ ...s.msgBox, ...(msg.type === 'error' ? s.msgError : s.msgSuccess) }}>
          {msg.text}
        </div>
      )}

      {/* ADD / EDIT FORM */}
      {showForm && (
        <div style={s.formCard}>
          <div style={s.formHeader}>
            <div style={s.formTitle}>{editingId ? 'Edit Prompt' : 'Add a Prompt'}</div>
            <button style={s.cancelBtn} onClick={resetForm}>Cancel</button>
          </div>

          <div style={s.formField}>
            <label style={s.label}>Title <span style={s.required}>*</span></label>
            <input
              style={s.input}
              type="text"
              placeholder="Give this prompt a clear, searchable name"
              value={form.title}
              onChange={e => set('title', e.target.value)}
            />
          </div>

          <div style={s.formRow}>
            <div style={s.formField}>
              <label style={s.label}>Use Case</label>
              <input
                style={s.input}
                type="text"
                placeholder="What specific task does this handle?"
                value={form.useCase}
                onChange={e => set('useCase', e.target.value)}
              />
            </div>
            <div style={s.formField}>
              <label style={s.label}>Workflow</label>
              <input
                style={s.input}
                type="text"
                placeholder="Which larger workflow does this belong to?"
                value={form.workflow}
                onChange={e => set('workflow', e.target.value)}
              />
            </div>
          </div>

          <div style={s.formField}>
            <label style={s.label}>Role Tag</label>
            <input
              style={s.input}
              type="text"
              placeholder="Your role with enough context to shape the output"
              value={form.roleTag}
              onChange={e => set('roleTag', e.target.value)}
            />
          </div>

          <div style={s.formField}>
            <label style={s.label}>The Prompt <span style={s.required}>*</span></label>
            <textarea
              style={{ ...s.textarea, minHeight: '120px' }}
              placeholder="Full prompt text. Mark every bracket clearly."
              value={form.prompt}
              onChange={e => set('prompt', e.target.value)}
            />
          </div>

          <div style={s.formField}>
            <label style={s.label}>Output Note</label>
            <input
              style={s.input}
              type="text"
              placeholder="One sentence — what does AI produce and where does it go?"
              value={form.outputNote}
              onChange={e => set('outputNote', e.target.value)}
            />
          </div>

          <div style={s.formField}>
            <label style={s.label}>Chain Prompt</label>
            <textarea
              style={{ ...s.textarea, minHeight: '70px' }}
              placeholder="The next logical step you would take with that output"
              value={form.chainPrompt}
              onChange={e => set('chainPrompt', e.target.value)}
            />
          </div>

          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Update Prompt' : 'Save to Library'}
          </button>
        </div>
      )}

      {/* SEARCH */}
      {!showForm && prompts.length > 0 && (
        <div style={s.searchBar}>
          <input
            style={s.searchInput}
            type="text"
            placeholder="Search by title, use case, or keyword..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <div style={s.countNote}>{filtered.length} of {prompts.length} prompt{prompts.length !== 1 ? 's' : ''}</div>
        </div>
      )}

      {/* PROMPT LIST */}
      {loading ? (
        <div style={{ color: '#8A8A8A', fontSize: '14px' }}>Loading your library...</div>
      ) : prompts.length === 0 && !showForm ? (
        <div style={s.emptyState}>
          <div style={s.emptyIcon}>📋</div>
          <div style={s.emptyTitle}>Your library is empty</div>
          <div style={s.emptySub}>Every prompt you build and refine belongs here. Click Add Prompt to get started.</div>
          <button style={s.addBtn} onClick={() => setShowForm(true)}>+ Add Your First Prompt</button>
        </div>
      ) : (
        <div style={s.promptList}>
          {filtered.length === 0 && (
            <div style={{ fontSize: '13px', color: '#8A8A8A', padding: '20px 0' }}>No prompts match your search.</div>
          )}
          {filtered.map(p => (
            <div key={p.id} style={s.promptCard}>
              <div style={s.promptCardHeader}>
                <div style={s.promptTitle}>{p.title}</div>
                <div style={s.promptDate}>{formatDate(p.createdAt)}</div>
              </div>

              {(p.useCase || p.workflow) && (
                <div style={s.promptMeta}>
                  {p.useCase && <span style={s.metaTag}>{p.useCase}</span>}
                  {p.workflow && <span style={{ ...s.metaTag, background: '#e6f1fb', color: '#185fa5' }}>{p.workflow}</span>}
                </div>
              )}

              {p.roleTag && (
                <div style={s.promptSection}>
                  <div style={s.promptSectionLabel}>Role Tag</div>
                  <div style={s.promptSectionText}>{p.roleTag}</div>
                </div>
              )}

              <div style={s.promptSection}>
                <div style={s.promptSectionLabel}>The Prompt</div>
                <div style={{ ...s.promptSectionText, ...s.promptText }}>{p.prompt}</div>
              </div>

              {p.outputNote && (
                <div style={s.promptSection}>
                  <div style={s.promptSectionLabel}>Output Note</div>
                  <div style={s.promptSectionText}>{p.outputNote}</div>
                </div>
              )}

              {p.chainPrompt && (
                <div style={s.promptSection}>
                  <div style={s.promptSectionLabel}>Chain Prompt</div>
                  <div style={{ ...s.promptSectionText, ...s.promptText }}>{p.chainPrompt}</div>
                </div>
              )}

              <div style={s.promptActions}>
                <button style={s.copyPromptBtn} onClick={() => handleCopy(p.prompt)}>Copy Prompt</button>
                {p.chainPrompt && (
                  <button style={{ ...s.copyPromptBtn, background: '#e6f1fb', color: '#185fa5', borderColor: '#b8d4f0' }} onClick={() => handleCopy(p.chainPrompt)}>Copy Chain</button>
                )}
                <button style={s.editBtn} onClick={() => handleEdit(p)}>Edit</button>
                <button
                  style={s.deleteBtn}
                  onClick={() => handleDelete(p.id)}
                  disabled={deleting === p.id}
                >
                  {deleting === p.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '700px', paddingBottom: '60px', fontFamily: 'DM Sans, sans-serif' },
  headerRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', gap: '16px' },
  addBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 22px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  msgBox: { padding: '10px 16px', fontSize: '13px', fontWeight: '500', marginBottom: '16px' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
  formCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '28px', marginBottom: '24px' },
  formHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', paddingBottom: '14px', borderBottom: '1px solid #E8E6E2' },
  formTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1B2A4A', fontWeight: '700' },
  cancelBtn: { background: 'transparent', color: '#8A8A8A', border: '1.5px solid #E8E6E2', padding: '7px 16px', fontSize: '12px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  formField: { marginBottom: '16px', flex: 1 },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '0' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '6px', fontFamily: 'DM Sans, sans-serif' },
  required: { color: '#C0392B' },
  input: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '11px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginTop: '8px' },
  searchBar: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' },
  searchInput: { flex: 1, border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FFFFFF', outline: 'none' },
  countNote: { fontSize: '12px', color: '#8A8A8A', whiteSpace: 'nowrap' },
  emptyState: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '60px 40px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  emptyIcon: { fontSize: '36px' },
  emptyTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A' },
  emptySub: { fontSize: '13px', color: '#8A8A8A', fontWeight: '300', maxWidth: '340px', lineHeight: '1.7' },
  promptList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  promptCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px' },
  promptCardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px', gap: '12px' },
  promptTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', fontWeight: '700', lineHeight: '1.3' },
  promptDate: { fontSize: '11px', color: '#8A8A8A', whiteSpace: 'nowrap', flexShrink: 0, paddingTop: '2px' },
  promptMeta: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
  metaTag: { fontSize: '11px', fontWeight: '600', background: '#F2F0EC', color: '#3D3D3D', padding: '3px 10px' },
  promptSection: { marginBottom: '12px' },
  promptSectionLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A', marginBottom: '4px' },
  promptSectionText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', fontWeight: '300' },
  promptText: { background: '#FAF8F5', border: '1.5px solid #E8E6E2', padding: '10px 14px', whiteSpace: 'pre-wrap', fontFamily: 'DM Sans, sans-serif' },
  promptActions: { display: 'flex', gap: '8px', flexWrap: 'wrap', paddingTop: '14px', borderTop: '1px solid #E8E6E2', marginTop: '4px' },
  copyPromptBtn: { background: '#eaf3de', color: '#3b6d11', border: '1.5px solid #c3e0b0', padding: '6px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  editBtn: { background: '#F2F0EC', color: '#1B2A4A', border: '1.5px solid #E8E6E2', padding: '6px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', marginLeft: 'auto' },
  deleteBtn: { background: '#fdecea', color: '#a32d2d', border: '1.5px solid #f5c6c6', padding: '6px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
};
